import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { SparkProjectDetails, SparkTagDimensionSelection } from '../types/sparkTypes';
import styles from '../styles/ProjectSetupPanel.module.css';
import { useAtlasStore, AtlasState } from '../../../store/atlasStore';
import { User } from '../../../types/user';
import { AddMembersModal } from './modals/AddMembersModal';
import { useAuth } from '../../../contexts/AuthContext';
import { ConfirmationModal } from './modals/ConfirmationModal';
import { toast } from 'react-toastify';
import useSparkTagOptions from '../hooks/useSparkTagOptions';
import TagContextSelector from './tags/TagContextSelector';

// --- Member Card Component ---
interface MemberCardProps {
    user: User | undefined;
    isCurrentUser: boolean;
    onOpenRemoveConfirm: (username: string) => void;
    isRemoving: boolean;
    canManageMembers: boolean;
}

const MemberCard: React.FC<MemberCardProps> = ({ user, isCurrentUser, onOpenRemoveConfirm, isRemoving, canManageMembers }) => {
    if (!user) {
        return <li className={`${styles.memberItem} ${styles.memberCard}`}>User data unavailable</li>;
    }

    const handleRemoveClick = () => {
        onOpenRemoveConfirm(user.username);
    };

    return (
        <li className={`${styles.memberItem} ${styles.memberCard}`}>
            <div className={styles.memberInfo}>
                <span className={styles.memberName}>{user.name}</span>
                <span className={styles.memberUsername}>{user.username}</span>
            </div>
            {!isCurrentUser && canManageMembers && (
                <button
                    className={`${styles.textButton} ${styles.removeButton}`}
                    onClick={handleRemoveClick}
                    disabled={isRemoving}
                    title={`Remove ${user.name}`}
                >
                    Remove
                </button>
            )}
        </li>
    );
};

interface ProjectSetupPanelProps {
    project: SparkProjectDetails;
}

export const ProjectSetupPanel: React.FC<ProjectSetupPanelProps> = ({ project }) => {
    // --- State ---
    const [isNameEditing, setIsNameEditing] = useState(false);
    const [editName, setEditName] = useState(project.name);
    const [isSaving, setIsSaving] = useState(false);
    const [isRemovingUser, setIsRemovingUser] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isSavingMembers, setIsSavingMembers] = useState(false);
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
    const [userToRemove, setUserToRemove] = useState<string | null>(null);

    const nameInputRef = useRef<HTMLInputElement>(null);

    // --- Store Access ---
    const updateProjectOverview = useAtlasStore((state) => state.updateSparkProjectOverview);
    const fetchTenantUsers = useAtlasStore((state) => state.fetchTenantUsers);
    const addUserToSparkProject = useAtlasStore((state) => state.addUserToSparkProject);
    const removeUserFromSparkProject = useAtlasStore((state) => state.removeUserFromSparkProject);
    const setSparkUserInteractionBlockingRefresh = useAtlasStore((state: AtlasState) => state.setSparkUserInteractionBlockingRefresh);

    const tenantUsers = useAtlasStore((state) => state.tenantUsers);

    // --- Auth Context ---
    const { user: authUser } = useAuth();

    // --- Effects ---
    useEffect(() => {
        fetchTenantUsers();
    }, [fetchTenantUsers]);

    useEffect(() => {
        if (!isNameEditing) {
            setEditName(project.name);
        }
    }, [project.name, isNameEditing]);

    const effectiveProjectPattern = project.tag_pattern;
    const projectDimensionFqns = useMemo(() => {
        return new Set((effectiveProjectPattern || []).map(entry => entry.dimension_fqn));
    }, [effectiveProjectPattern]);

    const { dimensions: tagDimensions } = useSparkTagOptions(effectiveProjectPattern, {
        dimensionFqns: projectDimensionFqns,
        schemaPattern: effectiveProjectPattern,
    });
    const [tagError, setTagError] = useState<string | null>(null);

    // Note: Don't use setIsSavingTags here - TagContextSelector manages its own
    // persist state internally via selfPersistingRef. Using setIsSavingTags causes
    // multiple re-renders that produce visible flicker.
    const handlePersistTagPattern = useCallback(async (nextPattern: SparkTagDimensionSelection[]) => {
        setTagError(null);
        try {
            await updateProjectOverview(project.fqn, { tag_pattern: nextPattern });
        } catch (error) {
            console.error('Failed to update tags:', error);
            const message = error instanceof Error ? error.message : 'Failed to update context';
            setTagError(message);
            toast.error(`Failed to update context: ${message}`);
            throw error;
        }
    }, [project.fqn, updateProjectOverview]);

    useEffect(() => {
        if (isNameEditing && nameInputRef.current) {
            nameInputRef.current.focus();
        }
    }, [isNameEditing]);

    useEffect(() => {
        if (isNameEditing) {
            setSparkUserInteractionBlockingRefresh(true);
            return () => {
                setSparkUserInteractionBlockingRefresh(false);
            };
        }
    }, [isNameEditing, setSparkUserInteractionBlockingRefresh]);

    // --- Memoized Lookups ---
    const userMap = useMemo(() => {
        const map = new Map<string, User>();
        tenantUsers.forEach(user => map.set(user.fqn, user));
        return map;
    }, [tenantUsers]);

    const projectMembers = useMemo(() => {
        return (project._user_fqns || [])
               .map((fqn: string) => userMap.get(fqn))
               .filter((user: User | undefined): user is User => user !== undefined);
    }, [project._user_fqns, userMap]);

    const currentMemberFqnsSet = useMemo(() => new Set(project._user_fqns || []), [project._user_fqns]);

    const saveProjectDetails = useCallback(async (payload: { name?: string; briefing?: string; tag_pattern?: SparkTagDimensionSelection[] }) => {
        const state = useAtlasStore.getState();
        await state.updateSparkProjectOverview(project.fqn, payload);
    }, [project.fqn]);

    // --- Handlers ---
    const handleSaveName = async () => {
        if (editName.trim() === project.name) {
            setIsNameEditing(false);
            return;
        }

        setIsSaving(true);
        const toastId = toast.loading("Updating project name...");
        try {
            await saveProjectDetails({
                name: editName.trim(),
                briefing: project.briefing || ''
            });
            setIsNameEditing(false);
            toast.update(toastId, {
                render: "Project name saved!",
                type: "success",
                isLoading: false,
                autoClose: 1500
            });
        } catch (error) {
            console.error("Failed to update project name:", error);
            const message = error instanceof Error ? error.message : "An unknown error occurred.";
            toast.update(toastId, {
                render: `Failed to save: ${message}`,
                type: "error",
                isLoading: false,
                autoClose: 5000
            });
            setEditName(project.name);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancelName = () => {
        setEditName(project.name);
        setIsNameEditing(false);
    };

    const handleOpenAddModal = () => {
        fetchTenantUsers(true);
        setIsAddModalOpen(true);
    };

    const handleAddMembers = async (usernamesToAdd: string[]) => {
        if (usernamesToAdd.length === 0) return;
        setIsSavingMembers(true);
        try {
            for (const username of usernamesToAdd) {
                await addUserToSparkProject(project.fqn, username);
            }
            setIsAddModalOpen(false);
        } catch (error) {
            console.error("Failed to add collaborators:", error);
        } finally {
            setIsSavingMembers(false);
        }
    };

    const handleOpenRemoveConfirm = (username: string) => {
        setUserToRemove(username);
        setShowRemoveConfirm(true);
    };

    const confirmRemoveUser = async () => {
        if (!userToRemove) return;
        setIsRemovingUser(true);
        setIsSavingMembers(true);
        try {
            await removeUserFromSparkProject(project.fqn, userToRemove);
        } catch (error) {
            console.error("Failed to remove collaborator:", error);
        } finally {
            setIsRemovingUser(false);
            setIsSavingMembers(false);
            setShowRemoveConfirm(false);
            setUserToRemove(null);
        }
    };

    const isClosed = !!project.is_closed;
    const isAdmin = !!project.current_user_is_admin;

    return (
        <div className={styles.setupPanelContainer}>
            {/* Project Name Section */}
            <section className={styles.section}>
                <div className={styles.detailItem}>
                    <label className={styles.detailLabel} htmlFor="projectNameInput">Project Name</label>
                    {isNameEditing ? (
                        <input
                            id="projectNameInput"
                            type="text"
                            className={styles.inputField}
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            disabled={isSaving}
                            ref={nameInputRef}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveName();
                                if (e.key === 'Escape') handleCancelName();
                            }}
                            onBlur={handleSaveName}
                        />
                    ) : (
                        <div
                            className={`${styles.readOnlyField} ${styles.clickableField}`}
                            onClick={() => { if (!isClosed) setIsNameEditing(true); }}
                        >
                            {project.name || 'Click to add project name...'}
                        </div>
                    )}
                </div>
            </section>

            {/* Project Scope Section */}
            {(effectiveProjectPattern && effectiveProjectPattern.length > 0) && (
                <section className={styles.section}>
                    <div className={styles.detailItem}>
                        <label className={styles.detailLabel}>Scope of this project</label>
                        <TagContextSelector
                            mode="project"
                            dimensions={tagDimensions}
                            value={effectiveProjectPattern}
                            disabled={isClosed}
                            error={tagError}
                            onPersist={handlePersistTagPattern}
                        />
                    </div>
                </section>
            )}

            {/* Team Section */}
            <section className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h3 className={styles.sectionTitle}>Collaborators on this project</h3>
                    <button
                        className={styles.standardButton}
                        onClick={handleOpenAddModal}
                        disabled={isSavingMembers || isNameEditing}
                    >
                        Add
                    </button>
                </div>
                {projectMembers.length > 0 ? (
                    <ul className={styles.memberList}>
                        {projectMembers.map(user => (
                            <MemberCard
                                key={user.fqn}
                                user={user}
                                isCurrentUser={authUser?.username === user.username}
                                onOpenRemoveConfirm={handleOpenRemoveConfirm}
                                canManageMembers={isAdmin}
                                isRemoving={isRemovingUser}
                            />
                        ))}
                    </ul>
                ) : (
                    <p className={styles.emptyListMessage}>No collaborators added yet.</p>
                )}
            </section>

            {/* Modals */}
            <AddMembersModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onAddMembers={handleAddMembers}
                currentMemberFqns={currentMemberFqnsSet}
                allTenantUsers={tenantUsers}
                isLoading={isSavingMembers}
            />

            <ConfirmationModal
                isOpen={showRemoveConfirm}
                onClose={() => setShowRemoveConfirm(false)}
                onConfirm={confirmRemoveUser}
                title="Remove Collaborator?"
                message={`Are you sure you want to remove ${userMap.get(userToRemove || '')?.name || userToRemove} from this project?`}
                confirmText="Remove"
                isConfirming={isRemovingUser}
                variant="destructive"
            />

        </div>
    );
};
